import React from "react";
import { AlertTriangle } from "lucide-react";
import { Loader2 } from "lucide-react";
import * as SelectPrimitive from "@radix-ui/react-select"
import { Slot } from "@radix-ui/react-slot";
import { ChevronDown } from "lucide-react"
import { LucideIcon } from "../../utils/icons";
import { formatCurrency } from "../../utils/helperFunctions";

export const Button = React.forwardRef(({ 
  children,
  name,
  className = "",
  variant = "default",
  size = "default",
  isLoading = false,
  isProcessing = false,
  disabled = false,
  asChild = false,
  action,
  onClick,
  color,
  outline = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";
  const isBusy = isLoading || isProcessing;
  const resolvedOnClick = onClick ?? action;
  const resolvedVariant =
    variant !== "default"
      ? variant
      : outline && color === "gray"
      ? "outlineMuted"
      : outline
      ? "outline"
      : color === "gray"
      ? "secondary"
      : "default";
  
  // Base classes always applied
  const baseClasses = "inline-flex items-center justify-center font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

  // Variant styles — all use rounded-lg (12px) to match the field/input radius
  const variants = {
    default:     "bg-primary text-white rounded-lg hover:bg-primary-400",
    destructive: "bg-danger text-white rounded-lg hover:bg-danger-text",
    outline:     "border border-primary bg-white text-primary rounded-lg hover:bg-primary-50",
    outlineMuted:"border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50",
    secondary:   "bg-secondary text-gray-900 rounded-lg hover:bg-secondary-600 hover:shadow-card",
    ghost:       "text-primary hover:bg-primary-50 rounded-lg",
    link:        "text-primary underline-offset-4 hover:underline rounded-lg",
    circle:      "rounded-full ring-2",
    icon:        "bg-primary text-white rounded-full hover:bg-primary-400",
    iconOutline: "border border-primary text-primary rounded-full hover:bg-primary/10",
  };

  // Size variations — all heights on 4px grid (h-6=24px h-8=32px h-10=40px h-12=48px)
  const sizes = {
    default: "h-10 px-4 py-2 text-sm",
    xs:      "h-6 px-3 text-xs",
    sm:      "h-8 px-3 text-sm",
    lg:      "h-12 px-8 text-base",
    icon:    "h-10 w-10",
  };

  const classes = [
    baseClasses,
    variants[resolvedVariant] ?? variants.default,
    sizes[size] ?? sizes.default,
    className
  ].join(" ");

  return (
    <Comp
      className={classes}
      ref={ref}
      disabled={disabled || isBusy}
      onClick={resolvedOnClick}
      {...props}
    >
      {isBusy ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {children|| name}
        </>
      ) : (
        children||name
      )}
    </Comp>
  );
});

export const InputWithLabel = ({
    inputId,
    inputType = "text",
    inputClass = "",
    inputPlaceholder = "",
    inputRequired = false,
    inputValue,
    onChange,
    inputDisabled = false,
    labelName = "Label",
    labelClass = "",
    prefix,
    ...rest
  }) => {
    return (
      <div>
        <label
          htmlFor={inputId}
          className={`field-label ${labelClass}`}
        >
          {labelName}
        </label>

        <div className="flex">
          {prefix && (
            <span className="inline-flex items-center px-3 text-sm text-gray-700 bg-gray-100 border border-r-0 border-gray-300 rounded-s-lg">
              {prefix}
            </span>
          )}

          <input
            type={inputType}
            id={inputId}
            className={`field-input ${prefix ? "rounded-none rounded-e-lg" : ""} ${inputClass}`}
            placeholder={inputPlaceholder}
            required={inputRequired}
            value={inputValue}
            onChange={onChange}
            disabled={inputDisabled}
            {...rest}
          />
        </div>
      </div>
    );
  };

export const Progressbar = ({...props}) => {
    return (
        <div className="space-y-1">
            <div className="flex justify-between text-sm font-semibold">
        <div>{props.title}</div>
        <div>{props.percentage}%</div>
    </div>
    <div className={props.className?props.className:`w-full  border rounded-full h-2.5 `}>
  <div className={`${props.barColor} h-2.5 rounded-full border text-primary`} style={{width: `${props.percentage}%`}}></div>
  
  {/* <div className="bg-blue-600 h-2.5 rounded-full" ></div> */}
</div>
<div>
    <div className="flex justify-end text-xs ">
      <p className="">{formatCurrency (props.amountSpent)}</p>/ <p>{formatCurrency(props.totalBudgeted)}</p>
    </div>
  </div>
        </div>


    )
}

// Alert Component
export const Alert = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-blue-50 text-blue-900 border-blue-200",
    destructive: "bg-red-50 text-red-900 border-red-200",
    warning: "bg-yellow-50 text-yellow-900 border-yellow-200",
    success: "bg-green-50 text-green-900 border-green-200"
  };

  return (
    <div className={`p-4 rounded-lg border ${variants[variant]} ${className}`} role="alert">
      {variant === "destructive" && (
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {children}
        </div>
      )}
      {variant !== "destructive" && children}
    </div>
  );
};

export const AlertDescription = ({ children }) => (
  <div className="text-sm font-medium">{children}</div>
);

export const CardTitle = ({ children, className = "" }) => (
  <h3 className={`font-semibold leading-none tracking-tight ${className}`}>
    {children}
  </h3>
);

export function CardContent({ children, className = '', ...props }) {
  return (
    <div className={`p-4 sm:p-6 ${className}`} {...props}>
      {children}
    </div>
  );
}

// Skeleton Component
export const Skeleton = ({ className = "" }) => (
  <div
    className={`skeleton-shimmer rounded-md ${className}`}
    role="status"
    aria-label="Loading..."
  >
    <span className="sr-only">Loading...</span>
  </div>
);

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
  return (
    <input
      type={type}
      className={`field-input h-10 ${className}`}
      ref={ref}
      {...props}
    />
  );
})
Input.displayName = "Input"

export const Select = React.forwardRef(({
  label, 
  value, 
  onChange, 
  children, 
  disabled, 
  name,
  required,
  className = '',
  ...props 
}, ref) => {
  return (
    <div className={className}>
      {label && (
        <label className="field-label">
          {label}
        </label>
      )}
      <select
        ref={ref}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        className="field-input"
        {...props}
      >
        {children}
      </select>
    </div>
  );
});

Select.displayName = 'Select';

export const SelectOption = ({ value, children }) => {
  return <option value={value}>{children}</option>;
};

export const IconPreview = ({ iconName, size = 24 }) => {
    return <LucideIcon iconName={iconName} size={size} />;
  };

  // const Select = SelectPrimitive.Root;

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={`flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={`relative z-50 min-w-[8rem] overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-900 shadow-dropdown data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ${className}`}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={`p-1 ${position === "popper" &&
          "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"}`}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={`relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ${className}`}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <ChevronDown className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

